import { SEARCH_DATA_KEY } from 'api/search'
import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useWebsocketContext } from 'context/Websocket/WebsocketProvider'
import isEqual from 'lodash.isequal'
import { useDebugLog } from 'debug/DebugLogContext'

type Row = { PK: string; SK: string; [k: string]: any }
type AllItems = {
  deals: Row[]
  organizations: Row[]
  products: Row[]
  expiresAt: string
  ttl: number
  cacheHit: boolean
}

const ALL_ITEMS_KEY = SEARCH_DATA_KEY
const ITEM_KEY = (PK: string, SK: string) => ['item', PK, SK] as const
const DEAL_UUID_KEY = (uuid: string) => ['deal', uuid] as const

// Server-side search query key prefixes for updating those caches too
const SEARCH_QUERY_PREFIXES = {
  organizations: ['search', 'organizations'],
  deals: ['search', 'deals'],
} as const

const BASE: AllItems = {
  deals: [],
  organizations: [],
  products: [],
  expiresAt: '',
  ttl: 0,
  cacheHit: false,
}

// ---- helpers --------------------------------------------------------------

// const upsertByPKSK = <T extends { PK: string; SK: string }>(arr: T[] = [], item?: T) => {
//   if (!item?.PK || !item?.SK) return arr
//   const i = arr.findIndex(x => x.PK === item.PK && x.SK === item.SK)
//   return i === -1 ? [...arr, item] : arr.map((x, idx) => (idx === i ? { ...x, ...item } : x))
// }
const upsertByPKSK = <T extends { PK: string; SK: string }>(arr: T[] = [], item?: T) => {
  if (!item?.PK || !item?.SK) return arr
  const i = arr.findIndex(x => x.PK === item.PK && x.SK === item.SK)
  if (i === -1) return [...arr, item]
  const merged = { ...arr[i], ...item }
  // if no real change, return original array to preserve referential stability
  if (isEqual(arr[i], merged)) return arr
  const next = arr.slice()
  next[i] = merged
  return next
}

const mergeProducts = (prev: Row[] = [], incoming: Row[] = []) => {
  if (!incoming?.length) return prev
  const seen = new Set(prev.map(p => p.SK))
  const add = incoming.filter(p => !seen.has(p.SK))
  return add.length ? [...prev, ...add] : prev
}

/**
 * Update server-side search queries (e.g., ['search', 'organizations', {...}])
 * This keeps paginated grid caches in sync with WebSocket updates
 */
const updateServerSideSearchQueries = (
  qc: ReturnType<typeof useQueryClient>,
  entityType: 'organizations' | 'deals',
  item: Row
) => {
  if (!item?.PK || !item?.SK) return

  const prefix = SEARCH_QUERY_PREFIXES[entityType]
  if (!prefix) return

  // Find and update all matching server-side search queries
  qc.setQueriesData(
    {
      predicate: q =>
        Array.isArray(q.queryKey) &&
        q.queryKey[0] === prefix[0] &&
        q.queryKey[1] === prefix[1],
    },
    (oldData: any) => {
      if (!oldData?.rows) return oldData

      // Find matching row by PK/SK or uuid
      const idField = entityType === 'organizations' ? 'org_uuid' : 'deal_uuid'
      const itemId = item[idField]

      let found = false
      const newRows = oldData.rows.map((row: any) => {
        // Match by PK+SK or by uuid
        const matchByPKSK = row.PK === item.PK && row.SK === item.SK
        const matchById = itemId && row[idField] === itemId

        if (matchByPKSK || matchById) {
          found = true
          // Merge the update with existing row data (preserves resolved entities)
          return { ...row, ...item }
        }
        return row
      })

      if (!found) {
        // Don't modify if no match found (item might be on a different page)
        return oldData
      }

      // Check if actually changed to avoid unnecessary re-renders
      if (isEqual(oldData.rows, newRows)) return oldData

      return { ...oldData, rows: newRows }
    }
  )
}

function applyEvent(state: AllItems, ev: any): AllItems {
  switch (ev.type) {
    // deals
    case 'deal.created': {
      const nextDeals = upsertByPKSK(state.deals, ev.deal)
      const nextProducts = mergeProducts(state.products, ev.products ?? [])
      return { ...state, deals: nextDeals, products: nextProducts }
    }
    case 'deal.updated': {
      return { ...state, deals: upsertByPKSK(state.deals, ev.deal) }
    }
    case 'deal.deleted': {
      const sk = ev.item?.SK
      const uuid = ev.item?.deal_uuid
      return {
        ...state,
        deals: state.deals.filter(d => d.SK !== sk),
        products: uuid ? state.products.filter(p => p.deal_uuid !== uuid) : state.products,
      }
    }

    // products (optional but recommended)
    case 'product.created':
    case 'product.updated': {
      return { ...state, products: upsertByPKSK(state.products, ev.item) }
    }
    case 'product.deleted': {
      const sk = ev.item?.SK
      return { ...state, products: state.products.filter(p => p.SK !== sk) }
    }

    // organizations (optional)
    case 'organization.created':
    case 'organization.updated': {
      return { ...state, organizations: upsertByPKSK(state.organizations, ev.item) }
    }
    case 'organization.deleted': {
      const sk = ev.item?.SK
      const pk = ev.item?.PK
      return {
        ...state,
        organizations: state.organizations.filter(o => !(o.PK === pk && o.SK === sk)),
      }
    }

    default:
      return state
  }
}

// ---- hook ----------------------------------------------------------------

export function useAllItemsLive() {
  const qc = useQueryClient()
  const { ws } = useWebsocketContext()
  const queue = useRef<any[]>([])
  const raf = useRef<number | null>(null)
  const { addLog, enabled } = useDebugLog()
  // Batch one flush per frame
  // const scheduleFlush = () => {
  //   if (raf.current != null) return
  //   raf.current = requestAnimationFrame(() => {
  //     qc.setQueryData<AllItems>(ALL_ITEMS_KEY, prev => {
  //       const start = prev ?? BASE
  //       if (queue.current.length === 0) {
  //         raf.current = null
  //         return start
  //       }

  //       let next = start
  //       for (const ev of queue.current) {
  //         next = applyEvent(next, ev)

  //         // keep detail caches in sync
  //         if (ev.type === 'deal.created' || ev.type === 'deal.updated') {
  //           const d = ev.deal
  //           if (d?.PK && d?.SK) qc.setQueryData(ITEM_KEY(d.PK, d.SK), d)
  //           if (d?.deal_uuid) qc.setQueryData(DEAL_UUID_KEY(d.deal_uuid), d)
  //         } else if (ev.type === 'deal.deleted' && ev.item) {
  //           const d = ev.item
  //           if (d?.PK && d?.SK) qc.removeQueries({ queryKey: ITEM_KEY(d.PK, d.SK), exact: true })
  //           if (d?.deal_uuid) qc.removeQueries({ queryKey: DEAL_UUID_KEY(d.deal_uuid), exact: true })
  //         }
  //       }

  //       queue.current = []
  //       raf.current = null
  //       // avoid unnecessary re-renders
  //       return isEqual(start, next) ? start : next
  //     })
  //   })
  // }
  const scheduleFlush = () => {
    if (raf.current != null) return
    raf.current = requestAnimationFrame(() => {
      qc.setQueryData<AllItems>(ALL_ITEMS_KEY, prev => {
        const start = prev ?? BASE
        let applied = 0
        let created = 0
        let updated = 0
        let deleted = 0
        let productUpserts = 0
        if (queue.current.length === 0) {
          raf.current = null
          return start
        }

        let next = start
        for (const ev of queue.current) {
          const _before = next
          next = applyEvent(next, ev)
          applied++
          if (ev.type === 'deal.created') created++
          if (ev.type === 'deal.updated') updated++
          if (ev.type === 'deal.deleted') deleted++
          if (ev.type?.startsWith?.('product.')) productUpserts++

          // keep detail caches in sync
          if (ev.type === 'deal.created' || ev.type === 'deal.updated') {
            const d = ev.deal
            // IMPORTANT: Merge with existing data to preserve fields like document links
            // that may not be included in partial websocket updates
            if (d?.PK && d?.SK) {
              qc.setQueryData(ITEM_KEY(d.PK, d.SK), (old: any) => (old ? { ...old, ...d } : d))
            }
            if (d?.deal_uuid) {
              qc.setQueryData(DEAL_UUID_KEY(d.deal_uuid), (old: any) => (old ? { ...old, ...d } : d))
            }
            // Also update server-side search queries for deals
            updateServerSideSearchQueries(qc, 'deals', d)
          } else if (ev.type === 'deal.deleted' && ev.item) {
            const d = ev.item
            if (d?.PK && d?.SK) qc.removeQueries({ queryKey: ITEM_KEY(d.PK, d.SK), exact: true })
            if (d?.deal_uuid) qc.removeQueries({ queryKey: DEAL_UUID_KEY(d.deal_uuid), exact: true })
          }

          // Keep organization server-side search queries in sync
          if (ev.type === 'organization.created' || ev.type === 'organization.updated') {
            const org = ev.item
            if (org?.PK && org?.SK) {
              updateServerSideSearchQueries(qc, 'organizations', org)
            }
          }
        }

        if (enabled && applied) {
          addLog({
            source: 'ws',
            level: 'info',
            label: 'WS flush applied',
            meta: {
              applied,
              created,
              updated,
              deleted,
              productUpserts,
              deals: next.deals.length,
              products: next.products.length,
              orgs: next.organizations.length,
            },
          })
        }

        queue.current = []
        raf.current = null
        // avoid unnecessary re-renders
        return isEqual(start, next) ? start : next
      })
    })
  }
  useEffect(() => {
    if (!ws) return

    const onOpen = () => {
      if (enabled) addLog({ source: 'ws', level: 'info', label: 'WS connected' })
    }
    const onClose = () => {
      if (enabled) addLog({ source: 'ws', level: 'warn', label: 'WS disconnected' })
    }

    try {
      ws.addEventListener('open', onOpen)
      ws.addEventListener('close', onClose)
    } catch { /* ignore errors when ws is already closed */ }
    const onMessage = (e: MessageEvent) => {
      let msg: any
      try {
        msg = JSON.parse(e.data)
      } catch {
        if (enabled) addLog({ source: 'ws', level: 'warn', label: 'WS non-JSON message' })
        return
      }
      if (enabled && msg?.type) {
        addLog({
          source: 'ws',
          level: 'info',
          label: `WS message: ${msg.type}`,
          meta: { preview: (typeof msg === 'object' ? msg.type : String(msg)).slice(0, 128) },
        })
      }
      // --- Deal events (normalize to { type, deal, products? })
      if (msg?.type === 'deal.created') {
        const deal = msg?.item?.deal ?? msg?.deal ?? msg?.item
        const products = msg?.item?.products ?? msg?.products ?? []
        if (deal?.PK && deal?.SK) queue.current.push({ type: 'deal.created', deal, products })
        scheduleFlush()
        return
      }
      if (msg?.type === 'deal.updated') {
        const deal = msg?.item?.deal ?? msg?.deal ?? msg?.item
        if (deal?.PK && deal?.SK) {
          if (import.meta.env.DEV) {
            console.log('[useAllItemsLive] Received deal.updated via WebSocket:', {
              SK: deal.SK,
              name: deal.name,
              _version: deal._version,
              _source: msg._source,
            })
          }
          queue.current.push({ type: 'deal.updated', deal })
        }
        scheduleFlush()
        return
      }
      if (msg?.type === 'deal.deleted') {
        const deal = msg?.item?.deal ?? msg?.deal ?? msg?.item
        if (deal?.PK && deal?.SK) queue.current.push({ type: 'deal.deleted', item: deal })
        scheduleFlush()
        return
      }

      // --- Legacy verbs → normalize (if any remain)
      if (msg?.type === 'CREATE' && msg?.EntityType === 'deal' && msg.item) {
        const deal = msg.item.deal ?? msg.item
        const products = msg.item.products ?? []
        if (deal?.PK && deal?.SK) queue.current.push({ type: 'deal.created', deal, products })
        scheduleFlush()
        return
      }
      if (msg?.type === 'SAVE' && msg?.EntityType === 'deal' && msg.item) {
        const deal = msg.item.deal ?? msg.item
        if (deal?.PK && deal?.SK) queue.current.push({ type: 'deal.updated', deal })
        scheduleFlush()
        return
      }
      if (msg?.type === 'DELETE' && msg?.EntityType === 'deal' && msg.item) {
        const deal = msg.item.deal ?? msg.item
        if (deal?.PK && deal?.SK) queue.current.push({ type: 'deal.deleted', item: deal })
        scheduleFlush()
        return
      }

      // --- (optional) pass-through for product/org events if you emit them
      if (msg?.type?.startsWith?.('product.') || msg?.type?.startsWith?.('organization.')) {
        queue.current.push(msg)
        scheduleFlush()
      }
    }

    ws.addEventListener('message', onMessage)
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current)
      try {
        ws.removeEventListener('message', onMessage)
        ws.removeEventListener('open', onOpen)
        ws.removeEventListener('close', onClose)
      } catch {
        // intentionally empty - ignore errors when ws is already closed
      }
    }
  }, [ws, qc, enabled])
}
